import COUNTRY_NAMES from 'src/utils/countryNames'

// ----------------------------------------------------------------------
// A person's name, a role, and a country, written the way the console shows them rather
// than the way the database stores them.
//
// Rental history renders renter names in the case they were typed in, which is
// mostly upper: `JÜRI TAMM` shouting down a column of sentence-case cells. It
// is a storage artefact — a passport field, a KYC provider's normalisation —
// and normalising it at the boundary is the difference between one rule and a
// `textTransform` in every table that ever shows a name.
//
// `admin` in a Role column is the same defect with the opposite polarity: a
// wire value printed raw. The role is a closed domain, so it gets a map, and a
// value outside it is a type error rather than a lowercase word.
// ----------------------------------------------------------------------

/**
 * Words that stay lowercase inside a name, and prefixes that keep the case of
 * what follows them. Deliberately short: a name is not a title, and a longer
 * list mostly produces confident mistakes about names it does not know.
 */
const PARTICLES = new Set([
  'van',
  'von',
  'der',
  'den',
  'de',
  'di',
  'du',
  'la',
  'le',
  'bin',
  'binti',
  'al',
])

const capitalise = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()

/**
 * One segment of a name, with the internal capitals a name can legitimately
 * carry: `O'Neill`, `McLeod` where it was typed that way, `Jean-Luc`.
 */
const segment = (word: string, index: number): string => {
  const lower = word.toLowerCase()
  if (index > 0 && PARTICLES.has(lower)) return lower
  return lower
    .split(/([-'’])/)
    .map((part) => (/[-'’]/.test(part) ? part : capitalise(part)))
    .join('')
}

/**
 * A person's name for display.
 *
 * A name already in mixed case is left alone: someone who writes their name
 * `d'Arcy` or `van Gogh` has made a decision, and overwriting it is worse than
 * the shouting this exists to fix. Only a name that is entirely one case is
 * normalised, because that is the one that cannot be a decision.
 */
export const personName = (value: string | null | undefined): string | null => {
  const name = value?.trim()
  if (!name) return null
  const letters = name.replace(/[^\p{L}]/gu, '')
  const shouted = letters.length > 1 && letters === letters.toUpperCase()
  const whispered = letters.length > 1 && letters === letters.toLowerCase()
  if (!shouted && !whispered) return name
  return name.split(/\s+/).map(segment).join(' ')
}

/**
 * The roles a manager can hold. Closed, so a new one is a compile error here
 * rather than a lowercase camelCase word in a table cell.
 */
export const ROLE_LABELS = {
  admin: 'Admin',
  fleetManager: 'Fleet manager',
  fleetAssistant: 'Fleet assistant',
} as const

export type Role = keyof typeof ROLE_LABELS

/**
 * A role for display. An unknown value returns null rather than its own key:
 * a cell reading `superAdmin` tells an operator the console does not know what
 * it is showing, and `absent` says that better than the key does.
 */
export const role = (value: string | null | undefined): string | null =>
  value && value in ROLE_LABELS ? ROLE_LABELS[value as Role] : null

/**
 * A country, named rather than coded.
 *
 * `Governing law` rendered `ee` — the value as stored, which is the column
 * and not the answer to the question the label asks. The code stays in
 * brackets because it is what the API and the contract annex are keyed on, so
 * a developer reading over an operator's shoulder still sees it.
 *
 * An unknown code returns null rather than itself: a field reading `zz` says
 * the console does not know what it is showing, and dressing it up as a name
 * hides that.
 */
export const country = (code: string | null | undefined): string | null => {
  const key = code?.trim().toLowerCase()
  if (!key) return null
  const name = (COUNTRY_NAMES as Record<string, string | undefined>)[key]
  return name ? `${name} (${key})` : null
}

/**
 * A country by its name alone, for a label or an export column where the code
 * is noise.
 *
 * The twin of `country` above and deliberately a second function rather than a
 * flag on it: which of the two a site wants is a decision about that site, and
 * a boolean parameter is how a boundary ends up with two behaviours and one
 * name. Use `country` where a developer may need to key on the code, and this
 * where the reader is an operator picking a place off a list.
 *
 * An unknown code returns null on the same grounds: a label reading `zz` says
 * the console does not know what it is showing.
 */
export const countryName = (code: string | null | undefined): string | null => {
  const key = code?.trim().toLowerCase()
  if (!key) return null
  return (COUNTRY_NAMES as Record<string, string | undefined>)[key] ?? null
}
